import type {
  ChatRequest,
  ModelInfo,
  ProviderStatus,
  StreamEvent,
  UniversalEndpoint,
} from "./types";

export interface ModelListResult {
  models: ModelInfo[];
  status: ProviderStatus;
}

export async function getModels(
  provider: "openrouter" | "gemini",
): Promise<ModelListResult> {
  const response = await fetch(`/api/models?provider=${provider}`, {
    credentials: "include",
  });
  const data = (await response.json()) as {
    models?: ModelInfo[];
    status?: ProviderStatus;
    error?: string;
  };
  if (!response.ok) {
    const error = new Error(
      data.error ?? `Impossibile caricare i modelli (${response.status})`,
    );
    Object.assign(error, { status: response.status, providerStatus: data.status });
    throw error;
  }
  return {
    models: data.models ?? [],
    status: data.status ?? {
      provider,
      hasApiKey: false,
      source: "none",
    },
  };
}

export async function getProviderStatuses(): Promise<ProviderStatus[]> {
  const response = await fetch("/api/providers", { credentials: "include" });
  if (!response.ok) throw new Error(`Stato provider non disponibile (${response.status})`);
  return ((await response.json()) as { providers: ProviderStatus[] }).providers;
}

export async function saveProviderCredential(
  provider: "openrouter" | "gemini",
  apiKey: string,
): Promise<ProviderStatus> {
  const response = await fetch(`/api/providers/${provider}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ apiKey }),
  });
  const data = (await response.json()) as {
    provider?: ProviderStatus;
    error?: string;
  };
  if (!response.ok) throw new Error(data.error ?? `Chiave non valida (${response.status})`);
  return data.provider!;
}

export async function deleteProviderCredential(
  provider: "openrouter" | "gemini",
): Promise<ProviderStatus> {
  const response = await fetch(`/api/providers/${provider}`, {
    method: "DELETE",
    credentials: "include",
  });
  const data = (await response.json()) as {
    provider?: ProviderStatus;
    error?: string;
  };
  if (!response.ok) throw new Error(data.error ?? `Rimozione chiave fallita (${response.status})`);
  return data.provider!;
}

export async function getUniversalEndpoints(): Promise<UniversalEndpoint[]> {
  const response = await fetch("/api/universal/endpoints", { credentials: "include" });
  if (!response.ok) throw new Error(`Impossibile caricare gli endpoint universali (${response.status})`);
  const data = (await response.json()) as { endpoints: UniversalEndpoint[] };
  return data.endpoints;
}

export async function saveUniversalEndpoint(input: {
  id?: string;
  name: string;
  baseUrl: string;
  model: string;
  apiKey?: string;
  headers: Record<string, string>;
  inputPrice?: number;
  outputPrice?: number;
}): Promise<UniversalEndpoint> {
  const response = await fetch("/api/universal/endpoints", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error(await response.text());
  return ((await response.json()) as { endpoint: UniversalEndpoint }).endpoint;
}

export async function deleteUniversalEndpoint(id: string): Promise<void> {
  const response = await fetch(`/api/universal/endpoints/${encodeURIComponent(id)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!response.ok) throw new Error(await response.text());
}

export async function streamChat(
  request: ChatRequest,
  signal: AbortSignal,
  onEvent: (event: StreamEvent) => void,
): Promise<void> {
  const response = await fetch("/api/chat", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
    signal,
  });
  if (!response.ok || !response.body) {
    const text = await response.text();
    throw new Error(text || `Errore API ${response.status}`);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.trim()) continue;
        onEvent(parseStreamEvent(line));
      }
    }
    // TextDecoder may hold an incomplete multi-byte character until the stream
    // is finished. Flush it before parsing the final NDJSON record.
    buffer += decoder.decode();
    if (buffer.trim()) onEvent(parseStreamEvent(buffer));
  } catch (error) {
    try {
      await reader.cancel();
    } catch {
      // The reader may already be closed by the browser.
    }
    throw error;
  } finally {
    reader.releaseLock();
  }
}

function parseStreamEvent(line: string): StreamEvent {
  try {
    return JSON.parse(line) as StreamEvent;
  } catch {
    throw new Error("Risposta API non valida: evento di streaming non è JSON.");
  }
}
